var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MeasurementErrorCodesClient } from '@/app/api/battery-portal-api';
import { MeasurementErrorCodesHelper } from '@/app/utils/measurement-error-codes-helper';
/**
 * Service for retrieving error code descriptions with lazy loading and built-in caching
 */
export class MeasurementErrorCodeDescriptionHelper {
    /**
     * Gets the client instance
     */
    static getClient() {
        if (!this.client) {
            this.client = new MeasurementErrorCodesClient(window.apiUrl);
        }
        return this.client;
    }
    /**
     * Gets description for an error code (lazy loads on first call)
     */
    static getDescription(errorCode, alarmDefinitionName) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            yield this.ensureCacheLoaded();
            // Apply swap logic if alarm definition name is provided
            const adjustedCode = alarmDefinitionName
                ? this.applyCodeSwap(errorCode, alarmDefinitionName)
                : errorCode;
            const description = (_a = this.descriptionCache) === null || _a === void 0 ? void 0 : _a.get(adjustedCode);
            return description || `Error code ${MeasurementErrorCodesHelper.formatErrorCode(errorCode)} - Description not available`;
        });
    }
    /**
     * Gets descriptions for a range of error codes with caching
     */
    static getDescriptionRange(minCode, maxCode, alarmDefinitionName) {
        return __awaiter(this, void 0, void 0, function* () {
            // Create cache key including alarm definition name for context-aware caching
            const cacheKey = `${minCode}_${maxCode}_${alarmDefinitionName || 'default'}`;
            // Return cached result if available
            if (this.rangeTooltipCache.has(cacheKey)) {
                return this.rangeTooltipCache.get(cacheKey);
            }
            // If already loading this specific range, wait for existing promise
            if (this.rangeLoadingPromises.has(cacheKey)) {
                return this.rangeLoadingPromises.get(cacheKey);
            }
            // Create and store loading promise
            const loadingPromise = (() => __awaiter(this, void 0, void 0, function* () {
                var _a;
                if (minCode === maxCode) {
                    const result = yield this.getDescription(minCode, alarmDefinitionName);
                    this.rangeTooltipCache.set(cacheKey, result);
                    return result;
                }
                try {
                    // Try to get from API with alarm definition name for swap logic
                    const description = yield this.getClient().getRange(minCode, maxCode, alarmDefinitionName);
                    this.rangeTooltipCache.set(cacheKey, description);
                    return description;
                }
                catch (error) {
                    console.error('Failed to load error code range description from API', error);
                    // Fallback to cached descriptions with manual swap logic
                    yield this.ensureCacheLoaded();
                    const descriptions = [];
                    for (let code = minCode; code <= maxCode; code++) {
                        const adjustedCode = alarmDefinitionName
                            ? this.applyCodeSwap(code, alarmDefinitionName)
                            : code;
                        const desc = (_a = this.descriptionCache) === null || _a === void 0 ? void 0 : _a.get(adjustedCode);
                        if (desc) {
                            descriptions.push(`${MeasurementErrorCodesHelper.formatErrorCode(code)}: ${desc}`);
                        }
                    }
                    const result = descriptions.length > 0
                        ? descriptions.join("<br/>")
                        : `Error codes ${MeasurementErrorCodesHelper.formatErrorCode(minCode)}–${MeasurementErrorCodesHelper.formatErrorCode(maxCode)} - Descriptions not available`;
                    this.rangeTooltipCache.set(cacheKey, result);
                    return result;
                }
                finally {
                    // Remove loading promise after completion
                    this.rangeLoadingPromises.delete(cacheKey);
                }
            }))();
            this.rangeLoadingPromises.set(cacheKey, loadingPromise);
            return loadingPromise;
        });
    }
    /**
     * Applies code swap logic (9 <-> 10) based on alarm definition name
     */
    static applyCodeSwap(errorCode, alarmDefinitionName) {
        if (!alarmDefinitionName || !MeasurementErrorCodesHelper.shouldSwapCodes9And10(alarmDefinitionName)) {
            return errorCode;
        }
        if (errorCode === 9)
            return 10;
        if (errorCode === 10)
            return 9;
        return errorCode;
    }
    /**
     * Loads error descriptions from backend into cache (lazy loaded)
     */
    static ensureCacheLoaded() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.descriptionCache !== null) {
                return;
            }
            if (this.loadingPromise !== null) {
                return this.loadingPromise;
            }
            this.loadingPromise = (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    const descriptions = yield this.getClient().getAll();
                    this.descriptionCache = new Map(descriptions.map(d => { var _a; return [d.errorCode, (_a = d.description) !== null && _a !== void 0 ? _a : ""]; }));
                }
                catch (error) {
                    console.error('Failed to load error descriptions from backend', error);
                    this.descriptionCache = new Map();
                }
                finally {
                    this.loadingPromise = null;
                }
            }))();
            return this.loadingPromise;
        });
    }
    /**
     * Clears all caches (useful for testing or forced refresh)
     */
    static clearCache() {
        this.descriptionCache = null;
        this.loadingPromise = null;
        this.rangeTooltipCache.clear();
        this.rangeLoadingPromises.clear();
    }
    /**
     * Gets a synchronous cached tooltip if available, otherwise returns loading message
     * Useful for immediate display with background loading
     */
    static getCachedTooltipOrLoad(minCode, maxCode, alarmDefinitionName) {
        const cacheKey = `${minCode}_${maxCode}_${alarmDefinitionName || 'default'}`;
        // Return cached if available
        if (this.rangeTooltipCache.has(cacheKey)) {
            return this.rangeTooltipCache.get(cacheKey);
        }
        // Start loading in background
        this.getDescriptionRange(minCode, maxCode, alarmDefinitionName).catch(error => {
            console.error('Background tooltip loading failed', error);
        });
        return 'Loading description...';
    }
}
MeasurementErrorCodeDescriptionHelper.client = null;
MeasurementErrorCodeDescriptionHelper.descriptionCache = null;
MeasurementErrorCodeDescriptionHelper.loadingPromise = null;
// Cache for range descriptions with alarm definition context
MeasurementErrorCodeDescriptionHelper.rangeTooltipCache = new Map();
MeasurementErrorCodeDescriptionHelper.rangeLoadingPromises = new Map();
